import { useContext, useEffect } from 'react'
import { uniqueId } from 'lodash'
import { type FlashMessage } from '@axilon-apps/libs-ui-types'
import { FlashMessageType } from '@axilon-apps/libs-ui-constants'
import type { Maybe } from '@axilon-apps/libs-shared-types'

import { FlashMessagesContext } from '../context/flash-messages-context'

type Params = Omit<FlashMessage, 'type' | 'group' | 'id' | 'message'> & {
  message: Maybe<string>
}

type CreateArgs = Omit<FlashMessage, 'id' | 'group' | 'message'> & { message: Maybe<string> }

const useFlashMessages = (group: string) => {
  const context = useContext(FlashMessagesContext)

  if (!context) {
    throw new Error(
      `useFlashMessages must be used within a FlashMessagesProvider. 
        Make sure your app is wrapped with <FlashMessagesProvider>`
    )
  }

  const { messages, setMessages } = context

  const create = (flashMessage: CreateArgs) => {
    if (!flashMessage.message) {
      return
    }

    setMessages((flashMessages: FlashMessage[]) => [
      ...flashMessages,
      { id: uniqueId(), group, ...flashMessage } as FlashMessage
    ])
  }

  const all = () => messages.filter((message) => message.group === group)
  const clear = (flash?: FlashMessage) => {
    setMessages((flashMessages: FlashMessage[]) =>
      flash
        ? flashMessages.filter((message) => message.id !== flash.id)
        : flashMessages.filter((message) => message.group !== group)
    )
  }
  const success = (params: Params) => create({ type: FlashMessageType.Success, ...params })
  const error = (params: Params) => create({ type: FlashMessageType.Error, ...params })
  const info = (params: Params) => create({ type: FlashMessageType.Info, ...params })
  const warn = (params: Params) => create({ type: FlashMessageType.Warning, ...params })
  const plain = (params: Params) => create({ type: FlashMessageType.Plain, ...params })

  useEffect(() => {
    return () => clear()
  }, [])

  return {
    all,
    clear,
    success,
    error,
    info,
    warn,
    plain
  }
}

export default useFlashMessages
